import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Search,
  Calendar
} from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { PortalGuruBottomNav } from './PortalGuruBottomNav';
import { getCategoryColor } from './newsData';

interface Berita {
  id: string;
  title: string;
  content: string;
  image?: string;
  target?: string[];
  status: 'published' | 'draft' | 'archived';
  category?: string;
  createdAt: Timestamp;
}

interface PortalGuruBeritaProps {
  isWali?: boolean;
  onItemClick?: (item: Berita) => void;
}

export const PortalGuruBerita: React.FC<PortalGuruBeritaProps> = ({ isWali = false, onItemClick }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Semua');
  const [beritaList, setBeritaList] = useState<Berita[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch only published news
  useEffect(() => {
    // Client-side sorting/filtering to avoid missing index errors
    const q = query(
      collection(db, 'berita'),
      where('status', '==', 'published')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Berita[];

      // Sort
      items.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return dateB.getTime() - dateA.getTime();
      });

      // Filter Target
      items = items.filter(item => {
          if (!item.target || item.target.length === 0) return true;
          if (isWali) {
              return item.target.includes('Wali Santri');
          }
          return item.target.includes('Guru');
      });

      setBeritaList(items);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp) return '';
    return new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(timestamp.toDate());
  };

  const stripHtml = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>?/gm, '');
  };

  // Filtering Logic
  const filteredNews = beritaList.filter(item => {
    const itemCategory = item.category || 'Umum';
    const matchesCategory = selectedCategory === 'Semua' || itemCategory === selectedCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = ['Semua', 'Akademik', 'Kegiatan', 'Pengumuman', 'Prestasi', 'Artikel'];

  return (
    <div className="min-h-screen bg-[#f6f8f6] text-[#111813] font-sans antialiased pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-3 flex items-center justify-between transition-colors">
        <div className="flex items-center gap-3">
          {/* Back button removed as requested */}
          <h1 className="text-lg font-bold tracking-tight text-[#111813]">Berita</h1>
        </div>
        {/* Search icon removed as requested */}
      </div>

      <PageTransition>
      {/* Search & Filters */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative group mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="text-gray-400 group-focus-within:text-[#40513B] transition-colors" size={20} />
          </div>
          <input
            className="block w-full pl-10 pr-3 py-3 border-none rounded-xl text-sm bg-white text-[#111813] shadow-sm ring-1 ring-gray-100 focus:ring-2 focus:ring-[#40513B]/20 placeholder-gray-400 transition-all outline-none"
            placeholder="Cari berita..."
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-[#40513B] text-white shadow-sm shadow-[#40513B]/30'
                  : 'bg-white border border-gray-200 text-[#63886f] hover:border-[#40513B]/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* News List */}
      <div className="px-4 mt-2 flex flex-col gap-4">
        {loading ? (
           <p className="text-center text-gray-500 py-10">Memuat berita...</p>
        ) : filteredNews.length > 0 ? (
          filteredNews.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                  if (onItemClick) {
                      onItemClick(item);
                  } else {
                      navigate('/portal-guru/berita/detail', {
                        state: {
                            ...item,
                            date: formatDate(item.createdAt),
                        }
                      });
                  }
              }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-3 hover:border-[#40513B]/50 transition-colors cursor-pointer"
            >
              <div
                className="w-full h-40 rounded-lg bg-center bg-cover bg-no-repeat bg-gray-200"
                style={{ backgroundImage: item.image ? `url("${item.image}")` : 'none' }}
              >
                  {!item.image && <div className="w-full h-full flex items-center justify-center text-gray-400"><Calendar size={48}/></div>}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-start">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${getCategoryColor(item.category || 'Umum')}`}>
                    {item.category || 'Umum'}
                  </span>
                  <span className="text-xs text-[#63886f]">{formatDate(item.createdAt)}</span>
                </div>
                <h3 className="text-lg font-bold text-[#111813] leading-snug">{item.title}</h3>
                <p className="text-sm text-[#63886f] line-clamp-2">{stripHtml(item.content)}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500 text-sm">Tidak ada berita yang ditemukan.</p>
          </div>
        )}
      </div>

      <div className="mt-8 text-center text-sm text-gray-400 pb-8">
        — Tidak ada berita lainnya —
      </div>
      </PageTransition>

      {/* Bottom Nav */}
      {!isWali && <PortalGuruBottomNav activeTab="berita" />}

    </div>
  );
};
